import React, { FC, useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { Flex, Box, Heading, Spinner, Card, Image, IconButton } from '@chakra-ui/react';
import { MdEdit, MdEditLocation } from 'react-icons/md';

import { USER_DATE_TIME_FORMAT } from '@app/utils/constants';
import { useGuide } from '@app/hooks/use-guide';
import { useAutocomplete } from '@app/hooks/use-autocomplete';
import { OutletField } from '@app/pages/equipments/dialogs/components/OutletField';
import { useAlert } from '@app/hooks';

import { EquipmentsRepairFormInstance } from './types';
import { useEntityState } from '../../hooks/use-entity-state';
import TextField from '../common/text-field';
import SelectField from '../common/select-field';
import { ImageDescriptor } from '../../api/models/image';
import api from '../../api/api';
import GalleryModal from '../common/modals/gallery';
import FieldView from '../common/field-view';
import AutocompleteField from '../common/autocomplete-field';
import { validateForm } from './utils/validation';
import { Form } from '../common/form/form';
import { SendModal } from './modals/send';
import { CancelModal } from './modals/remove';

interface EquipmentsRepairFormProps {
  instance: Partial<EquipmentsRepairFormInstance>;
  submitText?: string;
  onSubmit: (instance: Partial<EquipmentsRepairFormInstance>) => void;
  onCancel: () => void;
}

const EquipmentsRepairForm: FC<EquipmentsRepairFormProps> = (props) => {
  const { submitText, onSubmit, onCancel } = props;

  const { warning } = useAlert();

  const [images, setImages] = useState<ImageDescriptor[]>([]);
  const [instance, onChange, setInstance, isChanged] =
    useEntityState<EquipmentsRepairFormInstance>(props.instance);

  const [loading, setLoading] = useState<boolean>(false);
  const [showGallery, setShowGallery] = useState<boolean>(false);
  const [galleryCurrentIndex, setGalleryCurrentIndex] = useState<number>(0);

  const getBreaks = useGuide('service_breakdown');
  const getDepartments = useGuide('service_department');
  const getAutocomplete = useAutocomplete();

  useEffect(() => {
    setInstance(props.instance);
  }, [props.instance, setInstance]);

  useEffect(() => {
    (async () => {
      if (!instance?.service_data?.images) {
        return;
      }
      setLoading(true);
      const _images = await Promise.all(
        (props.instance.service_data?.images || []).map(
          async ({ image_pid, last_update }) => {
            try {
              const { data } = await api.file.get('' + image_pid);
              return {
                image_pid,
                last_update,
                src: 'data:image/jpeg;base64,' + data,
              };
            } catch (e) {
              return {
                image_pid,
                last_update,
              };
            }
          }
        )
      );
      setImages(_images);
      setLoading(false);
    })();
  }, [props.instance]);

  const onSubmitForm = useCallback(async () => {
    const { isValid, errors } = validateForm(instance);
    if (!isValid) {
      warning(Object.values(errors).join('. '));
      return;
    }

    await onSubmit({ ...instance });
  }, [onSubmit, instance]);

  const onSent = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const onOpenOutletEdit = useCallback(() => {
    window.open(`/acl/outlets/${instance.service_data?.outlet?.id}/`, '_blank');
  }, [instance.service_data?.outlet]);

  if (!instance) {
    return null;
  }

  return (<>
    <Heading size='h4' >Заявка на сервисное обслуживание ХО</Heading>
    <Form 
      instance={instance}   
      onSubmit={onSubmitForm} 
      alternativeElement={<>
        <SendModal needSave={isChanged} variant='button' id={instance.id?.toString()} order={instance} onSuccess={onSent} />
        <CancelModal variant='button' id={instance.id?.toString()} onDone={onCancel} />
      </>}
      onCancel={onCancel}    
      onChange={onChange}
    >
      <Form.Spacer span={{ md: 6 }} >
        <FieldView
          title="Дата и время заявки"
          value={moment(instance.visit_dt).format(USER_DATE_TIME_FORMAT)}
        />
      </Form.Spacer>
      <Form.Spacer span={{ md: 6 }} >
        <FieldView title="Пользователь" value={instance.user?.full_name} />
      </Form.Spacer>
      <Form.Spacer span={{ md: 4 }} >
        <FieldView
          title="ID оборудования"
          value={instance.equip_id}
          canCopy
        />
      </Form.Spacer>
      <Form.Spacer span={{ md: 4 }} >
        <FieldView
          title="Серийный номер"
          value={instance.service_data?.serial_num}
          canCopy
        />
      </Form.Spacer>
      <Form.Spacer span={{ md: 4 }} >
        <FieldView
          title="Инвентарный номер"
          value={instance.service_data?.inventory_num}
        />
      </Form.Spacer>
      <Form.Spacer span={{ md: 4 }} >
        <FieldView
          title="Производитель"
          value={instance.service_data?.manufacturer?.name}
        />
      </Form.Spacer>
      <Form.Spacer span={{ md: 4 }} >
        <FieldView
          title="Модель"
          value={instance.service_data?.model?.name}
        />
      </Form.Spacer>
      <Form.Spacer span={{ md: 4 }} >
        <FieldView title="Бренд" value={instance.service_data?.brand?.name} />
      </Form.Spacer>
      <Form.Item<EquipmentsRepairFormInstance, EntityBase>
        label="Вид поломки"
        field='service_data.problem'
        required
        component={SelectField}
        componentProps={{
          onFetchOptions: getBreaks,
        }}
        span={{ md: 12 }}
      />
      <Form.Spacer span={{ md: 12 }}/>
      <Form.Spacer span={{ md: 12 }} >
        <Flex gap={2} alignItems='center' w='100%' __css={{
          display: 'flex',
          '& div': { flexGrow: 1 },
        }}>
          <Form.Item<EquipmentsRepairFormInstance, EntityBase>
            label="Торговая точка"
            field='service_data.outlet'
            required
            component={OutletField}
            componentProps={{
              
            }}
          />
          <IconButton
            icon={<MdEdit />}
            aria-label="search"
            onClick={onOpenOutletEdit}
          />
        </Flex>
      </Form.Spacer>
      <Form.Item<EquipmentsRepairFormInstance, EntityBase>
        label="Фактический адрес"
        field='service_data.address'
        required
        component={AutocompleteField}
        componentProps={{
          name: 'dd/address',
          valueProperty: 'full_address',
          onFetchOptions: getAutocomplete,
        }}
        span={{ md: 12 }}
      />
      <Form.Item<EquipmentsRepairFormInstance, string>
        label="Комментарий"
        field='service_data.comment'
        required
        component={TextField}
        componentProps={{
          multiline: true
        }}
        span={{ md: 12 }}
      />
      <Form.Item<EquipmentsRepairFormInstance, string>
        label="Контакт"
        field='contacts'
        required
        component={TextField}
        span={{ md: 6 }}
      />
      <Form.Item<EquipmentsRepairFormInstance, EntityBase>
        label="Сервисная организация"
        field='department'
        required
        component={SelectField}
        componentProps={{
          onFetchOptions: getDepartments,
        }}
        span={{ md: 6 }}
      />
      <Form.Spacer span={{ md: 12 }}>
          <Flex gap={2} style={{ position: 'relative' }}>
            {loading && (
              <Box
                position="absolute"
                width="100%"
                height={200}
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <Spinner />
              </Box>
            )}
            {images.map((image, index) => {
              return (
                <Box key={image.image_pid}>
                  <Card style={{ position: 'relative' }}>
                    <Image
                      style={{ height: '200px', cursor: 'pointer' }}
                      src={image.src}
                      _empty={{ width: '150px', background: 'url(/images/empty.png)', backgroundPosition: 'center' }}
                      onClick={() => {
                        setGalleryCurrentIndex(index);
                        setShowGallery(true);
                      }}
                    />
                  </Card>
                </Box>
              );
            })}
          </Flex>
      </Form.Spacer>
    </Form>
    <GalleryModal
      images={images}
      open={showGallery}
      onClose={() => setShowGallery(false)}
      title="Изображения"
      index={galleryCurrentIndex}
      icon={<div />}
    />
  </>);
};

export default EquipmentsRepairForm;
